import type { ConflictRevision, Revision, RevisionConflictState } from '../../types';
import { mutations } from '../../utils';

/**
 * Type assertion for conflict revisions.
 */
export function isConflictRevision(revision: Revision): revision is ConflictRevision;
export function isConflictRevision(
    revision: mutations.MutableData<Revision>
): revision is mutations.MutableData<ConflictRevision>;
export function isConflictRevision(
    revision: mutations.MutableData<Revision>
): revision is mutations.MutableData<ConflictRevision> {
    return mutations.get(revision, 'conflicts') !== undefined;
}

/**
 * Update conflict state of a page. It only works if the Revision has already been marked as conflicting.
 */
export function updateConflictInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    state: RevisionConflictState | false
): void {
    if (!isConflictRevision(revision)) {
        mutations.setIn(revision, ['conflicts'] as const, { pages: {} });
    }

    if (!isConflictRevision(revision)) {
        throw new Error('updateConflictInRevision expected a revision with conflicts');
    }

    mutations.setIn(revision, ['conflicts', 'pages', pageId] as const, state);
    const pages = mutations.getIn(revision, ['conflicts', 'pages'] as const);

    if (!pages) {
        throw new Error('revision.conflicts missing a pages map');
    }

    let removeAllConflicts = true;
    // Decide whether we'll remove all conflicts, or just the one that's being updated.
    for (const [key, value] of mutations.entries(pages)) {
        const valueAfter = key === pageId ? state : value;

        // If any value is not false, we need to keep revision.conflicts and just update the individual value.
        if (valueAfter !== false) {
            removeAllConflicts = false;
            break;
        }
    }

    if (removeAllConflicts) {
        mutations.del(revision, 'conflicts');
    } else {
        mutations.setIn(revision, ['conflicts', 'pages', pageId] as const, state);
    }
}

/**
 * Mark conflicts as resolved for a page in a revision
 */
export function resolveConflictInRevision(revision: Revision, pageId: string): void {
    updateConflictInRevision(revision, pageId, false);
}
